import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  EMPTY,
  Subject,
  catchError,
  finalize,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Request } from 'npx-family-happy-common';
import { CalendlyModalComponent } from '../../shared/components/calendly/calendly-modal/calendly-modal.component';
import { Psychologist } from '../../shared/models/psychologist.model';
import { PsychologistRequestDetail } from '../../shared/models/request-psychologist-detail.model';
import { PsychologistService } from '../../caregiver/psychologist/psychologist-service/psychologist-service.service';
import { RequestPsychologistService } from '../request-psychologist-service/request-psychologist.service';
import { RequestRecordService } from '../request-round/request-record-service/request-record.service';
import { RequestService } from '../request-service/request.service';
import { MessageService } from '../../authentication/message-service/message-service.service';
import {
  CaregiverType,
  EventType,
  PaymentStatus,
  RequestRecord,
} from '../../shared/models/request-round/request-record.interface';

@Component({
  selector: 'app-psychologist-booking',
  standalone: true,
  imports: [CommonModule, FormsModule, CalendlyModalComponent],
  templateUrl: './psychologist-booking.component.html',
  styleUrl: './psychologist-booking.component.scss',
})
export class PsychologistBookingComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() request!: Request;
  @Input() psychologistDetail!: PsychologistRequestDetail;
  @Input() assignedPsychologistId!: string | null;

  psychologists: Psychologist[] = [];
  selectedPsychologist: Psychologist | null = null;
  showCalendlyModal = false;
  loading = false;
  bookingSessionAmount = 0;
  bookingDiscounted = false;

  private unsubscribe = new Subject<void>();

  constructor(
    private psychologistService: PsychologistService,
    private requestPsychologistService: RequestPsychologistService,
    private recordService: RequestRecordService,
    private requestService: RequestService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadPsychologists();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['assignedPsychologistId'] || changes['psychologistDetail']) &&
      this.request &&
      !changes['assignedPsychologistId']?.firstChange
    ) {
      this.loadPsychologists();
    }
  }

  /** Specializzazione su cui filtrare: il campo del form ha la precedenza,
   *  altrimenti si usa l'assignmentSpecialty della richiesta. */
  private getSpecialty(): string | null {
    const detail = this.psychologistDetail as any;
    return detail?.psychologistSpecialty || detail?.assignmentSpecialty || null;
  }

  loadPsychologists(): void {
    const specialty = this.getSpecialty();
    const filters: any = specialty ? { specialties: [specialty] } : {};

    this.psychologistService
      .findPsychologists(filters, 20)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (list) => {
          const safeList = list ?? [];
          // "Entrambi": se c'è uno psicologo già assegnato e non è nella lista
          // filtrata, lo recuperiamo e lo mostriamo in cima.
          if (
            this.assignedPsychologistId &&
            !safeList.some((p) => p.docRef === this.assignedPsychologistId)
          ) {
            this.psychologistService
              .getSinglePsychologist(this.assignedPsychologistId)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe({
                next: (arr) => {
                  this.psychologists = arr?.length
                    ? [arr[0], ...safeList]
                    : safeList;
                },
                error: () => (this.psychologists = safeList),
              });
          } else {
            this.psychologists = safeList;
          }
        },
        error: () => (this.psychologists = []),
      });
  }

  isAssigned(psychologist: Psychologist): boolean {
    return (
      !!this.assignedPsychologistId &&
      psychologist.docRef === this.assignedPsychologistId
    );
  }

  openCalendly(psychologist: Psychologist): void {
    if (!psychologist.info?.calendarLink) {
      this.messageService.setMessage(
        'Questo psicologo non ha un link calendario configurato.'
      );
      return;
    }
    this.selectedPsychologist = psychologist;
    this.showCalendlyModal = true;
  }

  closeCalendly(): void {
    this.showCalendlyModal = false;
  }

  /** Eseguito quando l'utente completa la prenotazione su Calendly.
   *  Replica il flusso della webapp clienti:
   *  update richiesta (psychologistId) -> cambio stato ->
   *  creazione record appuntamento -> notifica Slack. */
  onScheduled(): void {
    this.showCalendlyModal = false;
    const psychologist = this.selectedPsychologist;

    if (!psychologist?.docRef || !this.psychologistDetail) {
      this.messageService.setMessage(
        'Dati mancanti per completare la prenotazione'
      );
      return;
    }

    this.loading = true;
    const wasTrial = !!this.psychologistDetail.trial;

    this.requestPsychologistService
      .updatePsychologistDetail(this.request.id, {
        ...this.psychologistDetail,
        psychologistId: psychologist.docRef,
      })
      .pipe(
        takeUntil(this.unsubscribe),
        // 1. Aggiorna lo stato della richiesta
        switchMap(() =>
          this.requestService.changeStatus(
            this.request.id,
            wasTrial ? 'SELEZIONE COMPLETATA' : 'DA PAGARE'
          )
        ),
        // 2. Crea il record di appuntamento (round)
        switchMap(() =>
          this.recordService.createRecord(
            this.buildRecord(psychologist, wasTrial)
          )
        ),
        // 3. Notifica Slack
        switchMap(() =>
          this.requestPsychologistService.sendSlackNotification(
            this.request.id
          )
        ),
        catchError((error) => {
          this.messageService.setMessage(
            error?.message ||
              "Errore durante la prenotazione dell'appuntamento"
          );
          return EMPTY;
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: () => {
          this.bookingSessionAmount = 0;
          this.bookingDiscounted = false;
          this.messageService.setMessage(
            'Appuntamento prenotato con successo!'
          );
          setTimeout(() => window.location.reload(), 1200);
        },
      });
  }

  private buildRecord(
    psychologist: Psychologist,
    wasTrial: boolean
  ): RequestRecord {
    const now = new Date().toISOString();
    return {
      id: null,
      requestId: this.request.id,
      startDate: now,
      endDate: now,
      eventType: wasTrial ? EventType.TRIAL : EventType.USAGE,
      caregiverId: psychologist.docRef,
      caregiverName: `${psychologist.info.name} ${psychologist.info.surname}`,
      caregiverType: CaregiverType.PSYCHOLOGIST,
      hoursWorked: 1,
      paymentStatus: wasTrial ? PaymentStatus.PAID : PaymentStatus.TO_PAY,
      paymentLink: null,
      discounted: this.bookingDiscounted,
      sessionAmount: this.bookingSessionAmount,
      notes: null,
      createdAt: now,
      updatedAt: now,
    };
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
